import * as Sentry from '@sentry/react';
import { ViteReactSSG } from 'vite-react-ssg';
import 'flag-icons/css/flag-icons.min.css';
import './index.css';
import { routes } from './App';
import { loadManifest } from '@/lib/pwa-manifest';
import { canHydratePrerender } from '@/lib/ssgHydration';
import i18n from '@/i18n';

/* ─── createRoot ───
 * vite-react-ssg calls this both at build time (to prerender each route into
 * static HTML) and at runtime (to hydrate the SPA). The `routes` array lives
 * in App.tsx; everything browser-only lives below this export and is gated
 * behind `isClient` so it never runs during SSG.
 */
export const createRoot = ViteReactSSG(
  {
    routes,
    basename: import.meta.env.BASE_URL,
  },
  ({ isClient }) => {
    // `isClient` is true even during SSG when `ssgOptions.mock: true` is set
    // (it stubs `window`), so we rely on Vite's `import.meta.env.SSR` flag
    // — that one is statically true only in the SSR/SSG bundle. Belt:
    // double-check the real window has `document` attached too.
    if (import.meta.env.SSR || !isClient || typeof document === 'undefined') return;

    // ── Browser-only initialisation ──

    // Hydrate only HTML that matches what React will render; otherwise ask
    // the patched vite-react-ssg for a fresh render (see ssgHydration.ts).
    (window as Window & { __VITE_REACT_SSG_CLIENT_RENDER__?: boolean })
      .__VITE_REACT_SSG_CLIENT_RENDER__ = !canHydratePrerender(i18n.language);

    // Initialize Sentry error monitoring
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        enabled: import.meta.env.PROD || import.meta.env.VITE_SENTRY_DEBUG === 'true',
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
        ],
        tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        sendDefaultPii: false,
        beforeSend(event) {
          const msg = event.exception?.values?.[0]?.value || '';
          if (
            msg.includes('ResizeObserver loop') ||
            msg.includes('Non-Error promise rejection captured') ||
            (msg.includes('Network request failed') && navigator.onLine === false)
          ) {
            return null;
          }
          return event;
        },
      });
    }

    // Load dynamic PWA manifest based on route and language
    loadManifest();

    // The embeddable availability calendar runs inside hosts' own websites:
    // no service worker there (it would install our app cache and reload the
    // host's page on every deploy).
    const isEmbed = window.location.pathname.startsWith('/embed/');

    // Service worker (vite-plugin-pwa builds /sw.js; injectRegister is off so
    // the embed can opt out).
    if ('serviceWorker' in navigator && import.meta.env.PROD && !isEmbed) {
      const registerSW = () => {
        navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err) => {
          console.warn('[sw] registration failed', err);
        });
      };
      // This callback can run after `load` (lazy routes) — don't wait for an
      // event that already fired.
      if (document.readyState === 'complete') registerSW();
      else window.addEventListener('load', registerSW, { once: true });
    }

    // Auto-reload when a new service worker activates. Daily blog updates land
    // as new content-hashed JS bundles, and we want returning visitors to see
    // them without manually pressing Ctrl+Shift+R.
    if ('serviceWorker' in navigator && import.meta.env.PROD && !isEmbed) {
      let initialControllerSet = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!initialControllerSet) {
          initialControllerSet = true;
          return;
        }
        window.location.reload();
      });
    }

    // Virtual keyboard fix: update --app-height when keyboard opens/closes
    function updateAppHeight() {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${height}px`);
    }
    updateAppHeight();
    window.visualViewport?.addEventListener('resize', updateAppHeight);
    window.addEventListener('resize', updateAppHeight);
  }
);
